import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Briefcase, Plus, Pencil, Trash2, BarChart3, Filter, CheckCircle2, MoreVertical, Search } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JobsEnhanced() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [foremen, setForemen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [deletingJob, setDeletingJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterCustomer, setFilterCustomer] = useState('all');
  const [filterStatus, setFilterStatus] = useState('ACTIVE'); // Default to Active jobs
  const [searchQuery, setSearchQuery] = useState('');
  const [jobSortCol, setJobSortCol] = useState('jobNumber');
  const [jobSortDir, setJobSortDir] = useState('asc');
  
  const [formData, setFormData] = useState({
    customerId: '',
    jobNumber: '',
    jobType: 'TM',
    description: '',
    poNumber: '',
    poValue: '',
    assignedForemanIds: [],
    status: 'ACTIVE'
  });
  const [nextJobNumber, setNextJobNumber] = useState(null);

  const isOffice = user?.role === 'OFFICE' || user?.role === 'OWNER';
  const isForeman = user?.role === 'FOREMAN';
  const canCreateJob = isOffice || isForeman;

  useEffect(() => {
    fetchJobs();
    fetchCustomers();
    if (isOffice) {
      fetchForemen();
    }
  }, [token, isOffice]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchForemen = async () => {
    try {
      const response = await axios.get(`${API}/foremen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForemen(response.data);
    } catch (error) {
      console.error('Failed to fetch foremen:', error);
    }
  };

  const fetchNextJobNumber = async (customerId, jobType) => {
    if (!customerId) {
      setNextJobNumber(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/jobs/next-number`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { customerId, jobType }
      });
      setNextJobNumber(response.data);
      // Auto-fill the job number
      setFormData(prev => ({
        ...prev,
        jobNumber: response.data.nextJobNumber
      }));
    } catch (error) {
      console.error('Failed to fetch next job number:', error);
      setNextJobNumber(null);
    }
  };

  // Fetch next job number when customer or job type changes
  useEffect(() => {
    if (dialogOpen && !editingJob && formData.customerId) {
      fetchNextJobNumber(formData.customerId, formData.jobType);
    }
  }, [dialogOpen, editingJob, formData.customerId, formData.jobType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenDialog = (job = null, e = null) => {
    if (e) e.stopPropagation();
    if (job) {
      setEditingJob(job);
      setFormData({
        customerId: job.customerId,
        jobNumber: job.jobNumber,
        jobType: job.jobType || 'TM',
        description: job.description,
        poNumber: job.poNumber,
        poValue: job.poValue.toString(),
        assignedForemanIds: job.assignedForemanIds || [],
        status: job.status
      });
      setNextJobNumber(null);
    } else {
      setEditingJob(null);
      setFormData({
        customerId: '',
        jobNumber: '',
        jobType: 'TM',
        description: '',
        poNumber: '',
        poValue: '',
        assignedForemanIds: isForeman ? [user.id] : [],
        status: 'ACTIVE'
      });
      setNextJobNumber(null);
    }
    setDialogOpen(true);
  };

  const handleJobClick = (job) => {
    navigate(`/dashboard/job/${job.id}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        poValue: parseFloat(formData.poValue)
      };

      if (editingJob) {
        await axios.patch(`${API}/jobs/${editingJob.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Job updated successfully');
      } else {
        await axios.post(`${API}/jobs`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Job created successfully');
      }
      setDialogOpen(false);
      fetchJobs();
    } catch (error) {
      console.error('Failed to save job:', error);
      toast.error(error.response?.data?.detail || 'Failed to save job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingJob) return;

    try {
      await axios.delete(`${API}/jobs/${deletingJob.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingJob(null);
      fetchJobs();
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast.error('Failed to delete job');
    }
  };

  const handleQuickStatusChange = async (job, newStatus, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.patch(`${API}/jobs/${job.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Job marked as ${newStatus.toLowerCase().replace('_', ' ')}`);
      fetchJobs();
    } catch (error) {
      console.error('Failed to update job status:', error);
      toast.error('Failed to update job status');
    }
  };

  const toggleForeman = (foremanId) => {
    setFormData(prev => ({
      ...prev,
      assignedForemanIds: prev.assignedForemanIds.includes(foremanId)
        ? prev.assignedForemanIds.filter(id => id !== foremanId)
        : [...prev.assignedForemanIds, foremanId]
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'COMPLETED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ON_HOLD':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  // Filter jobs by customer, status, and search query
  const filteredJobs = useMemo(() => {
    const filtered = jobs.filter(j => {
      const matchesCustomer = filterCustomer === 'all' || j.customerId === filterCustomer;
      const matchesStatus = filterStatus === 'all' || j.status === filterStatus;
      
      let matchesSearch = true;
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const customerName = getCustomerName(j.customerId).toLowerCase();
        matchesSearch = 
          j.jobNumber?.toLowerCase().includes(query) ||
          j.description?.toLowerCase().includes(query) ||
          j.poNumber?.toLowerCase().includes(query) ||
          customerName.includes(query);
      }
      
      return matchesCustomer && matchesStatus && matchesSearch;
    });

    // Sort
    if (jobSortCol) {
      filtered.sort((a, b) => {
        let aVal, bVal;
        switch (jobSortCol) {
          case 'jobNumber': aVal = a.jobNumber || ''; bVal = b.jobNumber || ''; break;
          case 'customer': aVal = getCustomerName(a.customerId); bVal = getCustomerName(b.customerId); break;
          case 'description': aVal = a.description || ''; bVal = b.description || ''; break;
          case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
          default: aVal = ''; bVal = '';
        }
        return jobSortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
      });
    }
    return filtered;
  }, [jobs, filterCustomer, filterStatus, searchQuery, jobSortCol, jobSortDir, customers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get counts for status badges
  const statusCounts = {
    all: jobs.length,
    ACTIVE: jobs.filter(j => j.status === 'ACTIVE').length,
    COMPLETED: jobs.filter(j => j.status === 'COMPLETED').length,
    ON_HOLD: jobs.filter(j => j.status === 'ON_HOLD').length,
  };

  // Get unique customers from jobs for filter
  const jobCustomerIds = [...new Set(jobs.map(j => j.customerId).filter(Boolean))];
  const availableCustomers = customers.filter(c => jobCustomerIds.includes(c.id));

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-center text-slate-600">Loading jobs...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="jobs-page">
      <div className="flex flex-col gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
            {user?.role === 'FOREMAN' ? 'My Jobs' : 'All Jobs'}
          </h1>
          <p className="mt-1 md:mt-2 text-base md:text-lg text-slate-700">
            {user?.role === 'FOREMAN'
              ? 'Click on a job to view its recap'
              : 'Manage all company projects'}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end flex-wrap">
          {/* Status Filter - Pill buttons */}
          <div className="flex-1 sm:flex-none">
            <Label className="text-xs text-slate-500 mb-2 block">Filter by Status</Label>
            <div className="flex gap-1 flex-wrap">
              <Button
                variant={filterStatus === 'ACTIVE' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('ACTIVE')}
                className={`rounded-full text-xs px-3 ${filterStatus === 'ACTIVE' ? 'bg-green-600 hover:bg-green-700' : 'border-green-200 text-green-700 hover:bg-green-50'}`}
              >
                Active ({statusCounts.ACTIVE})
              </Button>
              <Button
                variant={filterStatus === 'COMPLETED' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('COMPLETED')}
                className={`rounded-full text-xs px-3 ${filterStatus === 'COMPLETED' ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-200 text-blue-700 hover:bg-blue-50'}`}
              >
                Completed ({statusCounts.COMPLETED})
              </Button>
              <Button
                variant={filterStatus === 'ON_HOLD' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('ON_HOLD')}
                className={`rounded-full text-xs px-3 ${filterStatus === 'ON_HOLD' ? 'bg-yellow-600 hover:bg-yellow-700' : 'border-yellow-200 text-yellow-700 hover:bg-yellow-50'}`}
              >
                On Hold ({statusCounts.ON_HOLD})
              </Button>
              <Button
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('all')}
                className={`rounded-full text-xs px-3 ${filterStatus === 'all' ? 'bg-slate-600 hover:bg-slate-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
              >
                All ({statusCounts.all})
              </Button>
            </div>
          </div>
          
          {/* Search Bar - Especially useful for completed jobs for bidding */}
          <div className="flex-1 sm:flex-none">
            <Label className="text-xs text-slate-500 mb-1 block">
              {filterStatus === 'COMPLETED' ? 'Search Completed Jobs (for bidding)' : 'Search Jobs'}
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={filterStatus === 'COMPLETED' ? "Search by job #, description, PO, customer..." : "Search jobs..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-9 w-full sm:w-[280px] rounded-sm border-2 ${filterStatus === 'COMPLETED' ? 'border-blue-300 focus:border-blue-500' : ''}`}
                data-testid="job-search-input"
              />
            </div>
          </div>
          
          {/* Customer Filter */}
          <div className="flex-1 sm:flex-none">
            <Label className="text-xs text-slate-500">Filter by Customer</Label>
            <Select value={filterCustomer} onValueChange={setFilterCustomer}>
              <SelectTrigger className="w-full sm:w-[200px] rounded-sm border-2">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="All Customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                {availableCustomers.map((customer) => (
                  <SelectItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Sort By */}
          <div className="flex-1 sm:flex-none">
            <Label className="text-xs text-slate-500">Sort By</Label>
            <Select value={`${jobSortCol}-${jobSortDir}`} onValueChange={(v) => { const [col, dir] = v.split('-'); setJobSortCol(col); setJobSortDir(dir); }}>
              <SelectTrigger className="w-full sm:w-[180px] rounded-sm border-2" data-testid="job-sort">
                <SelectValue placeholder="Sort..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="jobNumber-asc">Job # (A-Z)</SelectItem>
                <SelectItem value="jobNumber-desc">Job # (Z-A)</SelectItem>
                <SelectItem value="customer-asc">Customer (A-Z)</SelectItem>
                <SelectItem value="customer-desc">Customer (Z-A)</SelectItem>
                <SelectItem value="description-asc">Description (A-Z)</SelectItem>
                <SelectItem value="status-asc">Status (A-Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {canCreateJob && (
            <Button
              onClick={() => handleOpenDialog()}
              data-testid="create-job-button"
              className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm shadow-sm transition-all active:scale-95 py-4 md:py-2 text-base md:text-sm"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Job
            </Button>
          )}
        </div>
      </div>

      {filteredJobs.length === 0 ? (
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-12 text-center">
            <Briefcase className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600">
              {filterStatus !== 'all' && filterCustomer !== 'all' 
                ? `No ${filterStatus.toLowerCase().replace('_', ' ')} jobs for this customer`
                : filterStatus !== 'all' 
                  ? `No ${filterStatus.toLowerCase().replace('_', ' ')} jobs found`
                  : filterCustomer !== 'all' 
                    ? 'No jobs found for this customer' 
                    : 'No jobs found'}
            </p>
            {filterStatus !== 'all' && statusCounts.all > 0 && (
              <Button
                onClick={() => setFilterStatus('all')}
                variant="outline"
                className="mt-4 border-2 border-slate-300 text-slate-600 hover:bg-slate-50 rounded-sm"
              >
                View all {statusCounts.all} jobs
              </Button>
            )}
            {isOffice && filterCustomer === 'all' && filterStatus === 'all' && (
              <Button
                onClick={() => handleOpenDialog()}
                variant="outline"
                className="mt-4 border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm"
              >
                Create your first job
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
          {filteredJobs.map((job) => (
            <Card
              key={job.id}
              data-testid={`job-card-${job.jobNumber}`}
              onClick={() => handleJobClick(job)}
              className="border-slate-200 shadow-sm hover:shadow-md hover:border-[#1A3668] transition-all rounded-sm cursor-pointer active:scale-[0.98]"
            >
              <CardHeader className="pb-2 md:pb-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                      {job.jobNumber}
                      <BarChart3 className="w-4 h-4 md:w-5 md:h-5 text-slate-400" />
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1 truncate">{job.description}</p>
                    <p className="text-xs text-[#1A3668] font-medium mt-1">
                      {getCustomerName(job.customerId)}
                    </p>
                  </div>
                  <Badge className={`rounded-sm text-xs flex-shrink-0 ${getStatusColor(job.status)}`}>
                    {job.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0 md:pt-2">
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <p className="text-xs md:text-sm font-bold uppercase tracking-wider text-slate-500">PO Number</p>
                    <p className="text-base md:text-lg font-mono text-slate-700">{job.poNumber || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs md:text-sm font-bold uppercase tracking-wider text-slate-500">PO Value</p>
                    <p className="text-base md:text-lg font-bold text-[#16A34A]">{formatCurrency(job.poValue)}</p>
                  </div>
                </div>
                {isOffice && (
                  <div className="flex gap-2 mt-3 md:mt-4 pt-3 md:pt-4 border-t border-slate-200">
                    <Button
                      onClick={(e) => handleOpenDialog(job, e)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-2 border-slate-200 hover:border-[#1A3668] rounded-sm py-2"
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-2 border-slate-200 hover:border-[#1A3668] rounded-sm px-2"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        {job.status !== 'COMPLETED' && (
                          <DropdownMenuItem
                            onClick={(e) => handleQuickStatusChange(job, 'COMPLETED', e)}
                            className="text-green-600 cursor-pointer"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Mark as Completed
                          </DropdownMenuItem>
                        )}
                        {job.status !== 'ACTIVE' && (
                          <DropdownMenuItem
                            onClick={(e) => handleQuickStatusChange(job, 'ACTIVE', e)}
                            className="cursor-pointer"
                          >
                            <Briefcase className="w-4 h-4 mr-2" />
                            Mark as Active
                          </DropdownMenuItem>
                        )}
                        {job.status !== 'ON_HOLD' && (
                          <DropdownMenuItem
                            onClick={(e) => handleQuickStatusChange(job, 'ON_HOLD', e)}
                            className="text-yellow-600 cursor-pointer"
                          >
                            <Filter className="w-4 h-4 mr-2" />
                            Put On Hold
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingJob(job);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-600 cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Job
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Job Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingJob ? 'Edit Job' : 'Create New Job'}
            </DialogTitle>
            <DialogDescription>
              {editingJob ? 'Update job details' : 'Add a new job to the system'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customerId" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Customer *
                  </Label>
                  <Select
                    value={formData.customerId}
                    onValueChange={(value) => setFormData({ ...formData, customerId: value })}
                  >
                    <SelectTrigger data-testid="customer-select" className="mt-2 rounded-sm border-2">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name} {customer.jobCode ? `(${customer.jobCode})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="jobType" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Job Type *
                  </Label>
                  <Select
                    value={formData.jobType}
                    onValueChange={(value) => setFormData({ ...formData, jobType: value })}
                  >
                    <SelectTrigger data-testid="job-type-select" className="mt-2 rounded-sm border-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TM">Time & Material (TM)</SelectItem>
                      <SelectItem value="LS">Lump Sum (LS)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="jobNumber" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Job Number {!editingJob && '(Auto-generated)'}
                </Label>
                <Input
                  id="jobNumber"
                  data-testid="job-number-input"
                  value={formData.jobNumber}
                  onChange={(e) => setFormData({ ...formData, jobNumber: e.target.value })}
                  placeholder={nextJobNumber?.nextJobNumber || 'Select customer & type'}
                  className={`mt-2 rounded-sm border-2 font-mono ${!editingJob ? 'bg-slate-50' : ''}`}
                  readOnly={!editingJob}
                />
                {nextJobNumber && !editingJob && (
                  <p className="text-xs text-[#1A3668] mt-1">
                    Next: {nextJobNumber.nextJobNumber} ({nextJobNumber.customerName} - {nextJobNumber.jobType})
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="description" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Description *
                </Label>
                <Textarea
                  id="description"
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Project description..."
                  required
                  className="mt-2 rounded-sm border-2"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="poNumber" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    PO Number
                  </Label>
                  <Input
                    id="poNumber"
                    data-testid="po-number-input"
                    value={formData.poNumber}
                    onChange={(e) => setFormData({ ...formData, poNumber: e.target.value })}
                    placeholder="PO-2024-001"
                    className="mt-2 rounded-sm border-2"
                  />
                </div>
                <div>
                  <Label htmlFor="poValue" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    PO Value *
                  </Label>
                  <Input
                    id="poValue"
                    data-testid="po-value-input"
                    type="number"
                    step="0.01"
                    value={formData.poValue}
                    onChange={(e) => setFormData({ ...formData, poValue: e.target.value })}
                    placeholder="250000"
                    required
                    className="mt-2 rounded-sm border-2"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Status
                </Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="status-select" className="mt-2 rounded-sm border-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="ON_HOLD">On Hold</SelectItem>
                    <SelectItem value="COMPLETED">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {foremen.length > 0 && (
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Assigned Foremen
                  </Label>
                  <div className="mt-2 space-y-2">
                    {foremen.map((foreman) => (
                      <label
                        key={foreman.id}
                        className="flex items-center gap-2 p-2 border-2 border-slate-200 rounded-sm hover:bg-slate-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.assignedForemanIds.includes(foreman.id)}
                          onChange={() => toggleForeman(foreman.id)}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                        <span className="text-sm text-slate-700">{foreman.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-2 border-slate-200 rounded-sm">
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-job-button"
                disabled={submitting}
                className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm"
              >
                {submitting ? 'Saving...' : editingJob ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete job <strong>{deletingJob?.jobNumber}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-job"
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
